import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, Users, Calendar, MessageSquare, Building2, Plus, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import { STATUS_CONFIG, NOTE_TYPES } from './constants';
import NoteModal from './NoteModal';

export default function RequestDetailModal({ isOpen, onClose, request, onUpdate, onCreateAgency }) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [statusNote, setStatusNote] = useState('');
    const [changingStatus, setChangingStatus] = useState(null);

    const fetchNotes = useCallback(async () => {
        if (!request?.id) return;
        try {
            const res = await api.get(`/internal/demo-requests/${request.id}/notes`);
            setNotes(res.data.notes || []);
        } catch {}
    }, [request?.id]);

    useEffect(() => { if (isOpen && request) fetchNotes(); }, [isOpen, request, fetchNotes]);

    const handleStatusChange = async (newStatus) => {
        setLoading(true);
        try {
            await api.post(`/internal/demo-requests/${request.id}/status`, { status: newStatus, note: statusNote || null });
            toast.success('Statut mis à jour');
            onUpdate();
            setChangingStatus(null);
            setStatusNote('');
        } catch { toast.error('Erreur lors de la mise à jour'); }
        finally { setLoading(false); }
    };

    if (!isOpen || !request) return null;

    const StatusIcon = STATUS_CONFIG[request.status]?.icon || AlertCircle;
    const isConverted = request.status === 'converted';

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
                <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                    className="bg-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/10 shadow-2xl flex flex-col"
                    onClick={e => e.stopPropagation()}>
                    {/* Header */}
                    <div className="p-6 border-b border-white/10 flex-shrink-0">
                        <div className="flex items-start justify-between">
                            <div><h2 className="text-xl font-bold text-white">{request.company_name}</h2><p className="text-white/60">{request.contact_name}</p></div>
                            <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${STATUS_CONFIG[request.status]?.color} text-white`}>
                                <StatusIcon size={14} />{STATUS_CONFIG[request.status]?.label}
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* Contact info */}
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { icon: Mail, label: 'Email', value: request.email, href: `mailto:${request.email}` },
                                { icon: Phone, label: 'Téléphone', value: request.phone || 'Non renseigné', href: `tel:${request.phone}` },
                                { icon: Users, label: 'Taille équipe', value: request.company_size || 'Non renseignée' },
                                { icon: Calendar, label: 'Date demande', value: new Date(request.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) },
                            ].map(item => (
                                <div key={item.label} className="p-4 bg-white/5 rounded-xl">
                                    <div className="flex items-center gap-2 text-white/60 text-sm mb-1"><item.icon size={14} />{item.label}</div>
                                    {item.href ? <a href={item.href} className="text-white hover:text-purple-400">{item.value}</a> : <p className="text-white">{item.value}</p>}
                                </div>
                            ))}
                        </div>

                        {request.message && (
                            <div className="p-4 bg-white/5 rounded-xl">
                                <div className="flex items-center gap-2 text-white/60 text-sm mb-2"><MessageSquare size={14} />Message</div>
                                <p className="text-white">{request.message}</p>
                            </div>
                        )}

                        {/* Create Agency CTA */}
                        {!isConverted && (
                            <div className="p-4 bg-gradient-to-r from-purple-500/20 to-orange-500/20 rounded-xl border border-purple-500/30">
                                <div className="flex items-center justify-between">
                                    <div><p className="text-white font-medium">Convertir en agence</p><p className="text-white/60 text-sm">Créer le compte et les accès pour cette agence</p></div>
                                    <Button onClick={() => onCreateAgency(request)} className="bg-purple-500 hover:bg-purple-600"><Building2 className="w-4 h-4 mr-2" />Créer l'agence</Button>
                                </div>
                            </div>
                        )}

                        {/* Status Change */}
                        {!isConverted && (
                            <div>
                                <h4 className="text-sm font-medium text-white/70 mb-3">Changer le statut</h4>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'converted').map(([key, config]) => (
                                        <button key={key} onClick={() => setChangingStatus(key)} disabled={request.status === key}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${request.status === key ? `${config.color} text-white cursor-default` : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>
                                            {config.label}
                                        </button>
                                    ))}
                                </div>
                                <AnimatePresence>
                                    {changingStatus && changingStatus !== request.status && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-4 p-4 bg-white/5 rounded-xl">
                                            <p className="text-white/70 text-sm mb-2">Passer en <span className={`font-medium ${STATUS_CONFIG[changingStatus]?.textColor}`}>{STATUS_CONFIG[changingStatus]?.label}</span></p>
                                            <textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Note optionnelle..." rows={2}
                                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 mb-3" />
                                            <div className="flex gap-2">
                                                <Button size="sm" onClick={() => handleStatusChange(changingStatus)} disabled={loading} className="bg-purple-500 hover:bg-purple-600">{loading ? 'Mise à jour...' : 'Confirmer'}</Button>
                                                <Button size="sm" variant="outline" onClick={() => { setChangingStatus(null); setStatusNote(''); }} className="border-white/20 text-white">Annuler</Button>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Notes */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-medium text-white/70">Historique & Notes</h4>
                                <Button size="sm" onClick={() => setShowNoteModal(true)} className="bg-purple-500/20 text-purple-400 hover:bg-purple-500/30"><Plus size={14} className="mr-1" />Ajouter</Button>
                            </div>
                            <div className="space-y-3 max-h-60 overflow-y-auto">
                                {notes.length === 0 ? (
                                    <p className="text-white/40 text-sm text-center py-4">Aucune note</p>
                                ) : notes.map((note) => {
                                    const NoteIcon = NOTE_TYPES.find(t => t.value === note.note_type)?.icon || MessageSquare;
                                    return (
                                        <div key={note.id} className="p-3 bg-white/5 rounded-lg">
                                            <div className="flex items-center gap-2 text-white/60 text-xs mb-1">
                                                <NoteIcon size={12} /><span className="capitalize">{note.note_type}</span><span>•</span>
                                                <span>{new Date(note.created_at).toLocaleDateString('fr-FR')}</span>
                                            </div>
                                            <p className="text-white text-sm">{note.content}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="p-4 border-t border-white/10 flex-shrink-0 flex justify-end">
                        <Button onClick={onClose} variant="outline" className="border-white/20 text-white">Fermer</Button>
                    </div>
                </motion.div>
            </div>
            <NoteModal isOpen={showNoteModal} onClose={() => setShowNoteModal(false)} request={request} onNoteAdded={fetchNotes} />
        </>
    );
}
